import React, { memo } from 'react';
import styled from 'styled-components';

import colors from '@front/common/src/utils/colors';
import {
  formatPricePerHour,
  formatPricePerMonth,
  formatHoursPerCalendarUnit,
} from '@front/common/src/utils/formatting';
import { ChildcareBasketPrices } from '@front/common/src/types/commons';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import ServiceFeeIncludedText from '../../../service-fee-included-text';
import type { ChildcareBasketProps } from '../../childcare-basket';

const t = translate('basket');

const StyledAccordion = styled(Accordion)`
  margin: 10px 0 20px;
`;

export const CalculationWrapper = styled.div`
  margin-top: 10px;
`;

const CalculationLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  margin-bottom: 15px;
`;

const CalculationLineInList = styled(CalculationLine)`
  margin-bottom: 0;
`;

const StyledUl = styled.ul`
  padding-left: 20px;
  margin-top: 0;
`;

const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
`;

const HourlyTotalLine = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  margin-top: 2px;

  > span:first-of-type {
    margin-right: 5px;
  }
`;

const AllowanceEstimationWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`;

const ProEarnsLine = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`;

const UnderlineText = styled(Text)`
  text-decoration: underline;
`;

type RecurrentPrestationProps = Pick<
  ChildcareBasketProps,
  'childcareHoursPerWeek' | 'childrenCount' | 'youngestChildAge'
> & {
  prices: ChildcareBasketPrices;
};

const RecurrentPrestation = ({
  childcareHoursPerWeek,
  childrenCount = 0,
  prices,
  youngestChildAge,
}: RecurrentPrestationProps) => {
  const {
    allowanceAmount,
    hourlyPrice,
    monthlyEstimation,
    proPrice,
    totalHourlyPriceAfterTax,
    totalPrice,
    totalPriceAfterAllowance,
    totalPriceTaxCredit,
  } = prices;

  return (
    <StyledAccordion icon='basket' title={t('.childcare.recurring_prestation_title')} isOpen>
      {!!childcareHoursPerWeek && childrenCount > 0 && (
        <>
          <StyledUl>
            <li>
              <CalculationLineInList>
                <Text>{formatHoursPerCalendarUnit({ value: childcareHoursPerWeek, calendarUnit: 'week' })}</Text>
                <Text.SM color={colors.ink50}>{formatPricePerHour(hourlyPrice)}</Text.SM>
              </CalculationLineInList>
            </li>
            <li>
              <Text>{t(`.childcare.children_info.number_of_children.${childrenCount}`)}</Text>
            </li>
            <li>
              <Text>
                {t('basket.childcare.children_info.youngest_child_age.text', {
                  age: t(`basket.childcare.children_info.youngest_child_age.age.${youngestChildAge}`),
                })}
              </Text>
            </li>
          </StyledUl>
          <CalculationWrapper>
            <CalculationLine>
              <Text>{t('.total')}</Text>
              <Text>{formatPricePerMonth(totalPrice)}</Text>
            </CalculationLine>
            {allowanceAmount > 0 && (
              <>
                <CalculationLine>
                  <AllowanceEstimationWrapper>
                    <Text>{t('.childcare.allowance.estimation')}</Text>
                  </AllowanceEstimationWrapper>
                  <Text>{`- ${formatPricePerMonth(allowanceAmount)}`}</Text>
                </CalculationLine>
                <CalculationLine>
                  <Text>{t('.childcare.total_after_allowance')}</Text>
                  <Text>{formatPricePerMonth(totalPriceAfterAllowance)}</Text>
                </CalculationLine>
              </>
            )}
            <CalculationLine>
              <UnderlineText>{t('.childcare.total_price_tax_credit')}</UnderlineText>
              <Text>{`- ${formatPricePerMonth(totalPriceTaxCredit)}`}</Text>
            </CalculationLine>
          </CalculationWrapper>
          <Separator />
          <TotalLine>
            <Text.MDBold>{t('.final_estimation')}</Text.MDBold>
            <Text.MDBold>{formatPricePerMonth(monthlyEstimation)}</Text.MDBold>
          </TotalLine>
          <HourlyTotalLine>
            <Text.MDBold>{t('.childcare.equals')}</Text.MDBold>
            <Text.MDBold>{formatPricePerHour(totalHourlyPriceAfterTax)}</Text.MDBold>
          </HourlyTotalLine>
          <ServiceFeeIncludedText />
          <ProEarnsLine>
            <Text.SM>
              {t('.childcare.pro_earns', {
                pricePerHour: formatPricePerHour(proPrice),
              })}
            </Text.SM>
          </ProEarnsLine>
        </>
      )}
    </StyledAccordion>
  );
};

export default memo(RecurrentPrestation);
